import { Component, Input, Output, EventEmitter, forwardRef, ChangeDetectionStrategy } from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-toggle-switch',
  standalone: true,
  imports: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleSwitchComponent),
      multi: true
    }
  ],
  // One <label> wraps the switch AND its text, so the whole row is a single
  // 44px touch target (WCAG) with no dead gap between pill and caption. That
  // also removes the need for an id/for pairing — and with it the static id
  // counter this component used to carry.
  template: `
    <label class="toggle-switch-container" [class.disabled]="disabled">
      <span class="toggle-switch">
        <input
          type="checkbox"
          [checked]="checked"
          [disabled]="disabled"
          [attr.aria-label]="ariaLabel || null"
          [attr.aria-labelledby]="ariaLabelledby || null"
          (change)="onToggle($event)"
          class="toggle-input">
        <span class="toggle-slider" [class.checked]="checked">
          <span class="toggle-thumb"></span>
        </span>
      </span>
      @if (label) {
        <span class="toggle-label" [class.disabled]="disabled">
          {{ label }}
        </span>
      }
    </label>
    `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './toggle-switch.component.css'
})
export class ToggleSwitchComponent implements ControlValueAccessor {
  @Input() label?: string;
  /** Accessible name for the checkbox when no visible `label` is rendered. */
  @Input() ariaLabel?: string;
  /** Id of an existing visible element that names the checkbox. */
  @Input() ariaLabelledby?: string;
  @Input() disabled = false;
  @Input() checked = false;
  @Output() checkedChange = new EventEmitter<boolean>();

  private onChange = (value: boolean) => {};
  private onTouched = () => {};

  onToggle(event: Event): void {
    if (this.disabled) return;

    const target = event.target as HTMLInputElement;
    const newValue = target.checked;

    // Reset the checkbox to its previous state immediately
    // The parent component will update the checked value if the change is confirmed
    target.checked = this.checked;

    // Emit the intended new value for the parent to handle
    this.checkedChange.emit(newValue);
    this.onChange(newValue);
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: boolean): void {
    this.checked = value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
